import { supabase } from "@/integrations/supabase/client";

export interface AdminQuestion {
  id: string;
  questionType: string;
  title: string;
  chapter: string;
  chapterId: string;
  certificationId: string;
  kLevel: string;
  learningObjective: string;
  reference?: string;
  points: number;
  status: "draft" | "active";
  options?: { id: string; text: string }[];
  correctAnswer?: string;
  correctAnswers?: string[];
  matchPairs?: { left: string; right: string }[];
  categories?: { name: string; items: string }[];
  orderItems?: { id: string; text: string }[];
  optionExplanations?: Record<string, string>;
  generalExplanation?: string;
  isTrialSample?: boolean;
}

type Listener = () => void;
let _items: AdminQuestion[] = [];
let _loaded = false;
let _isAdminMode = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToQuestion(r: any): AdminQuestion {
  return {
    id: r.question_id,
    questionType: r.question_type,
    title: r.title,
    chapter: r.chapter ?? "",
    chapterId: r.chapter_id,
    certificationId: r.certification_id,
    kLevel: r.k_level,
    learningObjective: r.learning_objective ?? "",
    reference: r.reference ?? undefined,
    points: r.points,
    status: r.status as "draft" | "active",
    options: r.options ?? undefined,
    correctAnswer: r.correct_answer ?? undefined,
    correctAnswers: r.correct_answers ?? undefined,
    matchPairs: r.match_pairs ?? undefined,
    categories: r.categories ?? undefined,
    orderItems: r.order_items ?? undefined,
    optionExplanations: r.option_explanations ?? undefined,
    generalExplanation: r.general_explanation ?? undefined,
    isTrialSample: r.is_trial_sample ?? false,
  };
}

/** Map a row from the safe public view (no answer keys) */
export function rowToSafeQuestion(r: any): AdminQuestion {
  return {
    id: r.question_id,
    questionType: r.question_type,
    title: r.title,
    chapter: r.chapter ?? "",
    chapterId: r.chapter_id,
    certificationId: r.certification_id,
    kLevel: r.k_level,
    learningObjective: r.learning_objective ?? "",
    reference: r.reference ?? undefined,
    points: r.points,
    status: r.status as "draft" | "active",
    options: r.options ?? undefined,
    matchPairs: r.match_pairs ?? undefined,
    categories: r.categories ?? undefined,
    orderItems: r.order_items ?? undefined,
    // Answer keys intentionally omitted for learners
    correctAnswer: undefined,
    correctAnswers: undefined,
    optionExplanations: undefined,
    generalExplanation: undefined,
  };
}

async function loadFromDb(isAdmin?: boolean) {
  const admin = isAdmin ?? _isAdminMode;
  _isAdminMode = admin;

  if (admin) {
    // Admins read full question data through the questions_admin view.
    // The base table's answer-key columns are revoked from `authenticated`,
    // so direct SELECT * on `public.questions` would omit them. The view
    // runs with definer privileges and gates rows by has_role_mfa(), so an
    // admin session that is not MFA-verified (aal1) gets zero rows. In that
    // case fall back to the learner view so the normal study pages
    // (chapter practice, insights) still show data.
    const { data, error } = await supabase.from("questions_admin" as any).select("*");
    if (!error && data && data.length > 0) {
      _items = (data as any[]).map(rowToQuestion);
    } else {
      const { data: safe } = await supabase.from("questions_public").select("*");
      _items = (safe ?? []).map(rowToSafeQuestion);
    }
  } else {
    // Learners read from the safe view (no answer keys)
    const { data } = await supabase.from("questions_public").select("*");
    _items = (data ?? []).map(rowToSafeQuestion);
  }

  _loaded = true;
  notify();
}

// Don't auto-load on import — wait for auth to determine role
// loadFromDb();

export const questionStore = {
  get: () => _items,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(item: AdminQuestion) {
    const { error } = await supabase.from("questions").insert({
      question_id: item.id,
      question_type: item.questionType,
      title: item.title,
      chapter: item.chapter,
      chapter_id: item.chapterId,
      certification_id: item.certificationId,
      k_level: item.kLevel,
      learning_objective: item.learningObjective,
      reference: item.reference ?? null,
      points: item.points,
      status: item.status,
      options: (item.options ?? []) as unknown as null,
      correct_answer: item.correctAnswer ?? null,
      correct_answers: (item.correctAnswers ?? []) as unknown as null,
      match_pairs: (item.matchPairs ?? []) as unknown as null,
      categories: (item.categories ?? []) as unknown as null,
      order_items: (item.orderItems ?? []) as unknown as null,
      option_explanations: (item.optionExplanations ?? {}) as unknown as null,
      general_explanation: item.generalExplanation ?? "",
      is_trial_sample: item.isTrialSample ?? false,
    });
    if (!error) {
      _items = [..._items, item];
      notify();
    }
  },

  async update(id: string, patch: Partial<AdminQuestion>) {
    const dbPatch: Record<string, unknown> = {};
    if (patch.questionType !== undefined) dbPatch.question_type = patch.questionType;
    if (patch.title !== undefined) dbPatch.title = patch.title;
    if (patch.chapter !== undefined) dbPatch.chapter = patch.chapter;
    if (patch.chapterId !== undefined) dbPatch.chapter_id = patch.chapterId;
    if (patch.certificationId !== undefined) dbPatch.certification_id = patch.certificationId;
    if (patch.kLevel !== undefined) dbPatch.k_level = patch.kLevel;
    if (patch.learningObjective !== undefined) dbPatch.learning_objective = patch.learningObjective;
    if (patch.reference !== undefined) dbPatch.reference = patch.reference;
    if (patch.points !== undefined) dbPatch.points = patch.points;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.options !== undefined) dbPatch.options = patch.options;
    if (patch.correctAnswer !== undefined) dbPatch.correct_answer = patch.correctAnswer;
    if (patch.correctAnswers !== undefined) dbPatch.correct_answers = patch.correctAnswers;
    if (patch.matchPairs !== undefined) dbPatch.match_pairs = patch.matchPairs;
    if (patch.categories !== undefined) dbPatch.categories = patch.categories;
    if (patch.orderItems !== undefined) dbPatch.order_items = patch.orderItems;
    if (patch.optionExplanations !== undefined) dbPatch.option_explanations = patch.optionExplanations;
    if (patch.generalExplanation !== undefined) dbPatch.general_explanation = patch.generalExplanation;
    if (patch.isTrialSample !== undefined) dbPatch.is_trial_sample = patch.isTrialSample;

    const { error } = await supabase.from("questions").update(dbPatch).eq("question_id", id);
    if (!error) {
      _items = _items.map((q) => (q.id === id ? { ...q, ...patch } : q));
      notify();
    }
  },

  async remove(id: string) {
    const { error } = await supabase.from("questions").delete().eq("question_id", id);
    if (!error) {
      _items = _items.filter((q) => q.id !== id);
      notify();
      // Clean up: remove deleted question from any exam sets that reference it
      const { examSetStore } = await import("./examSetStore");
      const affectedSets = examSetStore.get().filter((es) => es.questionIds.includes(id));
      for (const es of affectedSets) {
        const newIds = es.questionIds.filter((qId) => qId !== id);
        await examSetStore.update(es.examSetId, { questionIds: newIds, questionCount: newIds.length });
      }
    }
  },
};
